import React, { useState, useRef } from 'react';
import { useTemplates } from '../hooks/useTemplates';
import { useToast } from '../context/ToastContext';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';
import type { Template } from '../types';

export const Templates: React.FC = () => {
  const { templates, loading, createTemplate, updateTemplate, deleteTemplate } = useTemplates();
  const { showToast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'custom' as Template['category'],
    content: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const templateData = {
        ...formData,
      };

      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, templateData);
        showToast('Template atualizado com sucesso!', 'success');
      } else {
        await createTemplate(templateData);
        showToast('Template criado com sucesso!', 'success');
      }

      setIsModalOpen(false);
      resetForm();
    } catch (error: any) {
      showToast(error.message || 'Erro ao salvar template', 'error');
    }
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      content: template.content,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este template?')) {
      try {
        await deleteTemplate(id);
        showToast('Template excluído com sucesso!', 'success');
      } catch (error: any) {
        showToast(error.message || 'Erro ao excluir template', 'error');
      }
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({ name: '', category: 'custom', content: '' });
  };

  const getCategoryLabel = (category: Template['category']) => {
    const labels = {
      'follow-up': 'Follow-up',
      'promotion': 'Divulgação',
      'scheduling': 'Agendamento',
      'custom': 'Personalizado'
    };
    return labels[category] || category;
  };

  if (loading) {
    return <div className="text-center py-8">Carregando templates...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-medium">Templates de Mensagens</h2>
        <Button onClick={() => { resetForm(); setIsModalOpen(true); }}>
          + Novo Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-text-secondary">Nenhum template cadastrado</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div key={template.id} className="template-card">
              <div className="template-header mb-3">
                <h3 className="font-medium">{template.name}</h3>
                <span className="badge badge-info">{getCategoryLabel(template.category)}</span>
              </div>
              <div className="template-content text-sm text-text-secondary mb-4 p-3 bg-surface-1 rounded-sm">
                {template.content}
              </div>
              <div className="template-actions flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(template)}>
                  ✏️
                </Button>
                <Button variant="danger" size="sm" onClick={() => handleDelete(template.id)}>
                  🗑️
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingTemplate ? 'Editar Template' : 'Novo Template'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button onClick={() => formRef.current?.requestSubmit()}>{editingTemplate ? 'Atualizar' : 'Salvar'}</Button>
          </>
        }
      >
        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Nome</label>
            <input
              className="input w-full"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Categoria</label>
            <select
              className="form-select w-full"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as Template['category'] })}
            >
              <option value="follow-up">Follow-up</option>
              <option value="promotion">Divulgação</option>
              <option value="scheduling">Agendamento</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Conteúdo (use variáveis: {'{nome}'}, {'{imovel}'}, {'{preco}'}, {'{link}'}, {'{telefone}'})</label>
            <textarea
              className="input w-full"
              rows={6}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};
